// =============================================================================
// MeuDriver — InvoiceDocument: Recibo/Fatura formal para o estudante
// =============================================================================

import { useRef } from 'react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Printer, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/utils'
import type { BookingInvoiceData } from '@/hooks/useInvoice'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatScheduledDate(date: string | null, time: string | null): string {
  if (!date) return '—'
  try {
    const dt = parseISO(`${date}T${time ?? '00:00'}`)
    return format(dt, "d 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })
  } catch {
    return date
  }
}

function statusLabel(status: string | null): string {
  const map: Record<string, string> = {
    paid: 'Pago',
    pending: 'Pendente',
    failed: 'Falhou',
    refunded: 'Reembolsado',
  }
  return map[status ?? ''] ?? status ?? '—'
}

function vehicleLabel(type: string | null): string {
  const map: Record<string, string> = {
    car: 'Automóvel',
    motorcycle: 'Motocicleta',
    truck: 'Caminhão',
    van: 'Van',
  }
  return map[type ?? ''] ?? type ?? '—'
}

// ─── Componente de linha de detalhe ──────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-800 text-right">{value}</span>
    </div>
  )
}

// ─── Estilos de impressão ─────────────────────────────────────────────────────

const PRINT_STYLES = `
@media print {
  body > *:not(#invoice-print-root) { display: none !important; }
  #invoice-print-root { display: block !important; position: static !important; }
  .no-print { display: none !important; }
  .invoice-container {
    max-width: 100% !important;
    box-shadow: none !important;
    border: none !important;
    padding: 24px !important;
  }
}
`

// ─── Componente principal ─────────────────────────────────────────────────────

interface InvoiceDocumentProps {
  data: BookingInvoiceData
  isLoading?: boolean
}

export function InvoiceDocument({ data, isLoading }: InvoiceDocumentProps) {
  const printRef = useRef<HTMLDivElement>(null)

  function handlePrint() {
    // Injeta estilos de impressão se ainda não estiverem presentes
    const styleId = 'invoice-print-style'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = PRINT_STYLES
      document.head.appendChild(style)
    }

    // Atribui id ao root para os estilos de impressão
    if (printRef.current) {
      printRef.current.id = 'invoice-print-root'
    }

    window.print()
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-4 w-48" />
        <div className="space-y-2 pt-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    )
  }

  const emittedAt = format(parseISO(data.created_at), "d 'de' MMMM 'de' yyyy", { locale: ptBR })

  return (
    <div ref={printRef} className="invoice-container bg-white rounded-xl border border-gray-200 shadow-sm max-w-xl mx-auto">
      {/* Cabeçalho */}
      <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
        <div>
          <p className="text-xl font-extrabold text-brand-700 tracking-tight">MeuDriver</p>
          <p className="text-xs text-muted-foreground mt-0.5">Marketplace de Instrutores de Direção</p>
        </div>
        <div className="text-right">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-xs font-semibold text-blue-700">
            <FileText className="h-3.5 w-3.5" />
            Recibo/Fatura
          </span>
        </div>
      </div>

      {/* Número e data */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
        <div>
          <p className="text-xs text-muted-foreground">Número da fatura</p>
          <p className="font-mono font-bold text-gray-800">{data.booking_number}</p>
        </div>
        <div className="sm:text-right">
          <p className="text-xs text-muted-foreground">Data de emissão</p>
          <p className="text-sm text-gray-700">{emittedAt}</p>
        </div>
      </div>

      {/* Partes envolvidas */}
      <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-gray-200">
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Aluno</p>
          <p className="text-sm font-semibold text-gray-800">{data.student_name ?? '—'}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Instrutor</p>
          <p className="text-sm font-semibold text-gray-800">{data.instructor_name ?? '—'}</p>
        </div>
      </div>

      {/* Detalhes do serviço */}
      <div className="px-6 py-4 border-b border-gray-200">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Descrição do serviço</p>
        <div className="space-y-0">
          <DetailRow label="Serviço" value="Aula de direção particular" />
          <DetailRow
            label="Data e horário"
            value={formatScheduledDate(data.scheduled_date, data.scheduled_time)}
          />
          <DetailRow label="Tipo de veículo" value={vehicleLabel(data.vehicle_type)} />
          {data.pickup_address && (
            <DetailRow label="Local de partida" value={data.pickup_address} />
          )}
        </div>
      </div>

      {/* Valores */}
      <div className="px-6 py-4 border-b border-gray-200">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Valores</p>
        <div className="space-y-0">
          {data.total_amount != null && (
            <DetailRow label="Valor total" value={formatCurrency(data.total_amount)} />
          )}
          {data.platform_fee != null && (
            <DetailRow label="Taxa da plataforma" value={formatCurrency(data.platform_fee)} />
          )}
          <DetailRow label="Status do pagamento" value={statusLabel(data.payment_status)} />
          {data.payment_method && (
            <DetailRow label="Método de pagamento" value={data.payment_method} />
          )}
          {data.mp_payment_id && (
            <DetailRow label="ID do pagamento (MP)" value={
              <span className="font-mono text-xs">{data.mp_payment_id}</span>
            } />
          )}
        </div>
      </div>

      {/* Total em destaque */}
      {data.total_amount != null && (
        <div className="px-6 py-4 flex justify-between items-center border-b border-gray-200 bg-brand-50">
          <span className="font-bold text-brand-800">Total</span>
          <span className="text-2xl font-extrabold text-brand-700">{formatCurrency(data.total_amount)}</span>
        </div>
      )}

      {/* Rodapé */}
      <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Este documento e apenas informativo e nao possui validade fiscal.<br />
          MeuDriver — contato@meudriver.com.br
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrint}
          className="no-print shrink-0 gap-1.5 text-gray-600"
          data-testid="invoice-print-btn"
        >
          <Printer className="h-4 w-4" />
          Imprimir / Salvar PDF
        </Button>
      </div>
    </div>
  )
}

// ─── Skeleton de carregamento ─────────────────────────────────────────────────

export function InvoiceDocumentSkeleton() {
  return (
    <div className="space-y-4 p-6 max-w-xl mx-auto">
      <div className="flex justify-between">
        <Skeleton className="h-7 w-28" />
        <Skeleton className="h-7 w-24 rounded-full" />
      </div>
      <div className="space-y-2 pt-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="space-y-2 pt-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )
}
